import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Package } from "lucide-react";

const STORAGE_KEY = "sg_recently_viewed";
const MAX_ITEMS = 12;

/**
 * Save a product to recently viewed in localStorage.
 * Call this from ProductDetailPage when a product loads.
 */
export function trackRecentlyViewed(product) {
    if (!product?.id || !product?.slug || !product?.name) return;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        let items = raw ? JSON.parse(raw) : [];
        // Remove duplicate
        items = items.filter(p => p.id !== product.id);
        // Add to front
        items.unshift({
            id: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            sale_price: product.sale_price,
            main_image: product.main_image,
            brand: product.brand,
        });
        // Trim
        if (items.length > MAX_ITEMS) items = items.slice(0, MAX_ITEMS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
}

/**
 * RecentlyViewed component - shows grid of recently viewed products.
 * Exclude the current product by passing excludeId.
 */
export default function RecentlyViewed({ excludeId, maxShow = 6 }) {
    const [items, setItems] = useState([]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            const all = raw ? JSON.parse(raw) : [];
            setItems(all.filter(p => p.id !== excludeId).slice(0, maxShow));
        } catch {}
    }, [excludeId, maxShow]);

    if (items.length === 0) return null;

    return (
        <div className="mt-12" data-testid="recently-viewed-section">
            <div className="flex items-center gap-2 mb-5">
                <Clock size={18} className="text-gray-400" />
                <h2 className="text-xl font-bold text-gray-900">Nedavno prohlizene</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {items.map(p => (
                    <Link key={p.id} to={`/produkt/${p.slug}`}
                        className="group bg-white rounded-xl border border-gray-100 hover:border-green-200 hover:shadow-md transition-all overflow-hidden"
                        data-testid={`recently-viewed-${p.slug}`}>
                        <div className="aspect-square bg-gray-50 flex items-center justify-center p-2 overflow-hidden">
                            {p.main_image ? (
                                <img src={p.main_image} alt={p.name}
                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform" loading="lazy" />
                            ) : (
                                <Package size={24} className="text-gray-300" />
                            )}
                        </div>
                        <div className="p-2.5">
                            <p className="text-xs font-medium text-gray-700 leading-tight line-clamp-2 group-hover:text-green-700 transition-colors mb-1">
                                {p.brand && <span className="text-gray-400">{p.brand} </span>}
                                {p.name}
                            </p>
                            <p className="font-bold text-sm" style={{ color: "var(--sg-primary)" }}>
                                {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kc
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
